import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Box, Typography, Grid, Paper, Chip, Skeleton } from '@mui/material';
import KeyIcon from '@mui/icons-material/VpnKey';
import BarChartIcon from '@mui/icons-material/BarChart';
import WebhookIcon from '@mui/icons-material/Webhook';
import { useAuth } from '../../auth/AuthContext';
import { listKeys, getBilling, listWebhooks } from '../../api/developerApi';

function StatCard({ icon, label, value, loading }) {
  return (
    <Paper elevation={0} sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ p: 1, bgcolor: '#EFF6FF', borderRadius: '6px', color: '#3B82F6', display: 'flex' }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          {loading
            ? <Skeleton width={60} height={28} />
            : <Typography variant="h6" fontWeight={700}>{value}</Typography>}
        </Box>
      </Box>
    </Paper>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const liveTenantId = user?.liveTenantId;
  const testTenantId = user?.testTenantId;

  const { data: liveKeys, isLoading: keysLoading } = useQuery(
    ['keys', liveTenantId], () => listKeys(liveTenantId), { enabled: !!liveTenantId }
  );
  const { data: billingData, isLoading: usageLoading } = useQuery(
    'billing', getBilling
  );
  const { data: liveWebhooks, isLoading: webhooksLoading } = useQuery(
    ['webhooks', liveTenantId], () => listWebhooks(liveTenantId), { enabled: !!liveTenantId }
  );

  const disabledWebhooks = liveWebhooks?.filter((w) => w.status === 'DISABLED') || [];
  const creditsUsed = billingData?.creditsUsed ?? '—';
  const keyCount = liveKeys?.length ?? '—';

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Dashboard</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Live environment overview
      </Typography>

      {disabledWebhooks.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: '#fef3c7', border: '1px solid #fcd34d' }}>
          <Typography variant="body2" fontWeight={600} color="#92400e">
            ⚠️ {disabledWebhooks.length} webhook{disabledWebhooks.length > 1 ? 's' : ''} disabled due to delivery failures.{' '}
            <Link to="/webhooks" style={{ color: '#92400e' }}>Review webhooks →</Link>
          </Typography>
        </Paper>
      )}

      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={4}>
          <StatCard icon={<KeyIcon fontSize="small" />} label="Active API keys (live)"
            value={keyCount} loading={keysLoading} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard icon={<BarChartIcon fontSize="small" />} label="Credits used this period (all envs)"
            value={creditsUsed} loading={usageLoading} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard icon={<WebhookIcon fontSize="small" />} label="Active webhooks (live)"
            value={liveWebhooks?.filter((w) => w.status === 'ACTIVE').length ?? '—'}
            loading={webhooksLoading} />
        </Grid>
      </Grid>

      <Typography variant="subtitle2" fontWeight={600} mb={1.5}>Environments</Typography>
      <Grid container spacing={2}>
        {[
          { label: 'Live', tenantId: liveTenantId, color: '#dcfce7', textColor: '#166534' },
          { label: 'Test', tenantId: testTenantId, color: '#dbeafe', textColor: '#1e40af' },
        ].map(({ label, tenantId, color, textColor }) => (
          <Grid item xs={12} sm={6} key={label}>
            <Paper elevation={0} sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>{label}</Typography>
                <Chip label={label} size="small"
                  sx={{ bgcolor: color, color: textColor, fontWeight: 600, fontSize: 11 }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {tenantId || '—'}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
